/**
 * Typography component factory
 * Creates typography components from configuration to reduce code duplication
 */

import React from 'react';
import type { ITypographyProps } from './types';
import type { TypographyVariant } from './config';
import { buildTypographyClasses } from './utils';

/**
 * Creates a typography component from a variant configuration
 */
export function createTypographyComponent(
	name: string,
	variant: TypographyVariant,
	defaultUpperCase = false
) {
	// In Tailwind v4, `text-{name}` utilities are generated from `--text-{name}` tokens in `@theme`.
	// We use numeric names (e.g. `text-32`) for font sizes defined by the design system theme.
	const baseClasses = `text-${variant.fontSize} leading-${variant.lineHeight}`;

	// Create component based on element type
	switch (variant.element) {
		case 'h1':
			return React.forwardRef<
				HTMLHeadingElement,
				ITypographyProps & Omit<React.ComponentProps<'h1'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<h1
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</h1>
				)
			);
		case 'h2':
			return React.forwardRef<
				HTMLHeadingElement,
				ITypographyProps & Omit<React.ComponentProps<'h2'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<h2
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</h2>
				)
			);
		case 'h3':
			return React.forwardRef<
				HTMLHeadingElement,
				ITypographyProps & Omit<React.ComponentProps<'h3'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<h3
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</h3>
				)
			);
		case 'h4':
			return React.forwardRef<
				HTMLHeadingElement,
				ITypographyProps & Omit<React.ComponentProps<'h4'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<h4
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</h4>
				)
			);
		case 'h5':
			return React.forwardRef<
				HTMLHeadingElement,
				ITypographyProps & Omit<React.ComponentProps<'h5'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<h5
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</h5>
				)
			);
		case 'h6':
			return React.forwardRef<
				HTMLHeadingElement,
				ITypographyProps & Omit<React.ComponentProps<'h6'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<h6
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</h6>
				)
			);
		case 'p':
			return React.forwardRef<
				HTMLParagraphElement,
				ITypographyProps & Omit<React.ComponentProps<'p'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<p
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</p>
				)
			);
		case 'span':
			return React.forwardRef<
				HTMLSpanElement,
				ITypographyProps & Omit<React.ComponentProps<'span'>, 'className'>
			>(
				(
					{
						children,
						color,
						hasEllipsis,
						fontStyle,
						isUpperCase = defaultUpperCase,
						fontWeight = variant.defaultWeight,
						className,
						...props
					},
					ref
				) => (
					<span
						ref={ref}
						className={buildTypographyClasses(baseClasses, {
							color,
							hasEllipsis,
							fontStyle,
							isUpperCase,
							fontWeight,
							className,
						})}
						{...props}
					>
						{children}
					</span>
				)
			);
		default:
			throw new Error(`Unsupported element type: ${variant.element}`);
	}
}
